import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
  HttpParamsOptions,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  tap,
  throwError,
} from 'rxjs';
import { STATUS } from './use-loading.model';

export interface IHttpOptions {
  body?: any;
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  context?: HttpContext;
  observe?: 'body';
  params?:
    | HttpParams
    | {
        [param: string]:
          | string
          | number
          | boolean
          | ReadonlyArray<string | number | boolean>;
      };
  responseType?: 'json';
  reportProgress?: boolean;
  withCredentials?: boolean;
}

export interface IHttpParams {
  url: string;
  body?: any;
  options?: IHttpOptions;
  success$?: BehaviorSubject<any | null>;
  status$?: BehaviorSubject<STATUS>;
  error$?: BehaviorSubject<string | null>;
}

@Injectable({
  providedIn: 'root',
})
export class EoHttpClient {
  constructor(private http: HttpClient) {}

  public options<T>(params: IHttpParams): Observable<T> {
    return this.httpRequest<T>('options', params);
  }

  public head<T>(params: IHttpParams): Observable<T> {
    return this.httpRequest<T>('head', params);
  }

  public get<T>(params: IHttpParams): Observable<T> {
    return this.httpRequest<T>('get', params);
  }

  public post<T>(params: IHttpParams): Observable<T> {
    return this.httpRequest<T>('post', params);
  }

  public put<T>(params: IHttpParams): Observable<T> {
    return this.httpRequest<T>('put', params);
  }

  public patch<T>(params: IHttpParams): Observable<T> {
    this.http.options;
    return this.httpRequest<T>('patch', params);
  }

  public delete<T>(params: IHttpParams): Observable<T> {
    return this.httpRequest<T>('delete', params);
  }

  public downloadFile(
    params: IHttpParams
  ): Observable<{ filename: string; data: Blob }> {
    const options = {
      responseType: 'blob' as 'json',
      observe: 'response' as 'body',
    };

    return this.httpRequest<HttpResponse<Blob>>('post', {
      ...params,
      options,
    }).pipe(
      map((response: HttpResponse<Blob>) => {
        console.log('All Response Headers:', [...response.headers.keys()]); // Debug all headers

        const contentDisposition =
          response.headers.get('content-disposition') ||
          response.headers.get('Content-Disposition');

        // Decode the filename from URL encoding
        const filename = contentDisposition
          ? decodeURIComponent(contentDisposition.split("filename*=UTF-8''")[1])
          : 'unknown';

        console.log('Decoded Filename:', filename); // Debug decoded filename

        return { filename, data: response.body as Blob };
      })
    );
  }

  private httpRequest<T>(
    method: 'options' | 'get' | 'post' | 'put' | 'patch' | 'delete' | 'head',
    params: IHttpParams
  ): Observable<T> {
    params.status$?.next(STATUS.loading);
    return this.http
      .request<T>(method ?? 'options', params.url, {
        body: params.body,
        ...params.options,
      })
      .pipe(
        tap((success) => {
          params.success$?.next(success);
          params.status$?.next(STATUS.success);
        }),
        catchError((error) => {
          params.status$?.next(STATUS.error);
          params.error$?.next(error.error);
          return throwError(() => new Error(error));
        })
      );
  }
}
